import path from 'node:path';
import { promises as fs } from 'node:fs';
import { zodToJsonSchema as _zodToJsonSchema } from 'zod-to-json-schema';
import type { ZodSchema } from 'zod';
import type { JSONSchema7 } from 'json-schema';

// Wrap zod-to-json-schema to keep TS from following deep generic
// instantiations into its internal types. The generated JSON Schema
// is the same.
function toJsonSchema(schema: ZodSchema): JSONSchema7 {
  const result = (_zodToJsonSchema as unknown as (s: ZodSchema, opts: Record<string, unknown>) => unknown)(
    schema,
    { target: 'jsonSchema7', $refStrategy: 'none' },
  );
  return result as JSONSchema7;
}

import { WorkflowBuilderError } from '../errors';
import { describeJobPoolRef } from '../builder/resolvers';
import { synthesiseLinearChain } from '../builder/transitions';
import type {
  JobPoolRef,
  CanonicalNodeBody,
  CanonicalSeedRequest,
  HttpRequestBehaviorConfig,
  JobsCreateBehaviorConfig,
  NodeDef,
  PromptBehaviorConfig,
  TransitionResolver,
  WorkflowDefinition,
} from '../types';

export interface CanonicaliseOptions {
  /** Owner principal id resolved against Keep (UUID). */
  ownerOrgId: string | null;
  /** Map of `${VAR}` interpolations used in endpoint URLs. */
  env?: Record<string, string>;
  /** Override the publish flag (forwarded to Loom). */
  publish?: boolean;
}

/**
 * Produce the wire-format `POST /api/admin/seed-workflow` body from a
 * `WorkflowDefinition`. Reads prompt templates from disk (relative to
 * the defining module), converts Zod schemas to JSON Schema, and
 * rewrites named pool resolvers to descriptors.
 */
export async function canonicaliseWorkflow(
  workflow: WorkflowDefinition,
  opts: CanonicaliseOptions,
): Promise<CanonicalSeedRequest> {
  const nodes: CanonicalNodeBody[] = [];
  for (const node of workflow.nodes) {
    nodes.push(await canonicaliseNode(workflow, node, opts));
  }
  const transitions = (workflow.transitions ?? synthesiseLinearChain(workflow.nodes)).map(
    (e): { from: string; to: string; resolverKey: null; artifacts?: unknown } => {
      const out: { from: string; to: string; resolverKey: null; artifacts?: unknown } = {
        from: e.from,
        to: e.to,
        resolverKey: null,
      };
      if (e.artifacts) {
        out.artifacts = e.artifacts.map((a) => {
          const serialised: Record<string, unknown> = { label: a.label };
          if (a.format !== undefined) serialised.format = a.format;
          if (a.outputSchema !== undefined) {
            serialised.schema = toJsonSchema(a.outputSchema);
          }
          return serialised;
        });
      }
      return out;
    },
  );

  return {
    name: workflow.name,
    description: workflow.description ?? null,
    ownerOrgId: opts.ownerOrgId,
    retainPublishedVersions: workflow.retain?.publishedVersions ?? null,
    ...(opts.publish !== undefined ? { publish: opts.publish } : {}),
    nodes,
    transitions,
  };
}

async function canonicaliseNode(
  workflow: WorkflowDefinition,
  node: NodeDef,
  opts: CanonicaliseOptions,
): Promise<CanonicalNodeBody> {
  if (node.entryBehavior === 'loom.prompt') {
    const promptOptions = node.promptOptions;
    if (!promptOptions) {
      throw new WorkflowBuilderError(workflow.name, node.name, 'promptNode missing options');
    }
    const promptDescriptor = describeJobPoolRef(promptOptions.jobPool as JobPoolRef);
    if (!promptDescriptor) {
      throw new WorkflowBuilderError(
        workflow.name,
        node.name,
        'jobPool must be a substrate-shipped named resolver, literalJobPool(...), or { id } / { name }',
      );
    }
    const templateResolved = path.resolve(workflow.__sourcePath, promptOptions.promptTemplate);
    let templateText: string;
    try {
      templateText = await fs.readFile(templateResolved, 'utf8');
    } catch (err) {
      throw new WorkflowBuilderError(
        workflow.name,
        node.name,
        `failed to read prompt template at ${templateResolved}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    const outputLabel = promptOptions.outputLabel ?? node.name;
    const config: PromptBehaviorConfig = {
      jobPoolRef: promptDescriptor,
      promptTemplate: templateText,
      promptTemplatePath: promptOptions.promptTemplate,
      outputSchema: toJsonSchema(promptOptions.outputSchema),
      outputLabel,
      rendering: promptOptions.rendering,
      ...(promptOptions.maxAttempts !== undefined ? { maxAttempts: promptOptions.maxAttempts } : {}),
      ...(promptOptions.deadline !== undefined ? { deadlineDuration: promptOptions.deadline } : {}),
    };
    return { ...envelope(node.name, promptOptions), entryBehavior: 'loom.prompt', config };
  }

  if (node.entryBehavior === 'http.request') {
    const httpOptions = node.httpOptions;
    if (!httpOptions) {
      throw new WorkflowBuilderError(workflow.name, node.name, 'httpNode missing options');
    }
    const env = opts.env ?? {};
    const config: HttpRequestBehaviorConfig = {
      endpoint: {
        method: httpOptions.endpoint.method,
        url: interpolate(httpOptions.endpoint.url, env),
      },
      ...(httpOptions.timeout !== undefined ? { timeout: httpOptions.timeout } : {}),
      ...(httpOptions.retryConfig !== undefined
        ? { retryConfig: { ...httpOptions.retryConfig } }
        : {}),
    };
    return { ...envelope(node.name, httpOptions), entryBehavior: 'http.request', config };
  }

  const jobsCreateOptions = node.jobsCreateOptions;
  if (!jobsCreateOptions) {
    throw new WorkflowBuilderError(workflow.name, node.name, 'jobsCreateNode missing options');
  }
  const jobPoolDescriptor = describeJobPoolRef(jobsCreateOptions.jobPool as JobPoolRef);
  if (!jobPoolDescriptor) {
    throw new WorkflowBuilderError(
      workflow.name,
      node.name,
      'jobPool must be a substrate-shipped named resolver, literalJobPool(...), or { id } / { name }',
    );
  }
  const config: JobsCreateBehaviorConfig = {
    jobPoolRef: jobPoolDescriptor,
    inputs: { ...jobsCreateOptions.inputs },
    ...(jobsCreateOptions.maxAttempts !== undefined
      ? { maxAttempts: jobsCreateOptions.maxAttempts }
      : {}),
    ...(jobsCreateOptions.deadline !== undefined
      ? { deadlineDuration: jobsCreateOptions.deadline }
      : {}),
  };
  return { ...envelope(node.name, jobsCreateOptions), entryBehavior: 'jobs.create', config };
}

/** Shared envelope (name, description, transition, gates) common to all behaviors. */
function envelope(
  name: string,
  options: {
    description?: string;
    transition?: TransitionResolver;
    qualityGates?: import('../types').QualityGate[];
    emitOutput?: import('../types').EmitOutput;
  },
): Pick<
  CanonicalNodeBody,
  'name' | 'description' | 'transitionResolver' | 'transitionContext' | 'qualityGates' | 'emitOutput'
> {
  return {
    name,
    ...(options.description !== undefined ? { description: options.description } : {}),
    ...(options.transition !== undefined
      ? {
          transitionResolver: options.transition.resolver,
          transitionContext: extractTransitionContext(options.transition),
        }
      : {}),
    ...(options.qualityGates !== undefined
      ? { qualityGates: serialiseQualityGates(options.qualityGates) }
      : {}),
    // Normalise the emit spec: `true` → emit-all (`{}`); object → as-is;
    // `false`/omitted → not emitted (field absent).
    ...(options.emitOutput === true
      ? { emitOutput: {} }
      : options.emitOutput && typeof options.emitOutput === 'object'
        ? { emitOutput: options.emitOutput }
        : {}),
  };
}

function interpolate(value: string, env: Record<string, string>): string {
  return value.replace(/\$\{([A-Z_][A-Z0-9_]*)\}/g, (_match, name: string) => {
    if (Object.prototype.hasOwnProperty.call(env, name)) return env[name];
    throw new Error(`seedWorkflows: missing env var "${name}" referenced in automation endpoint URL`);
  });
}

function extractTransitionContext(tr: import('../types').TransitionResolver): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (tr.to) out.to = tr.to;
  if (tr.rules) out.rules = tr.rules;
  return out;
}

function serialiseQualityGates(gates: import('../types').QualityGate[]): unknown {
  return gates.map((group) => ({
    group: group.group,
    gates: group.gates.map((g) => {
      if (g.strategy === 'artifact_check') {
        return {
          name: g.name,
          strategy: 'artifact_check',
          outputLabel: g.outputLabel,
          outputSchema: toJsonSchema(g.outputSchema),
        };
      }
      if (g.strategy === 'webhook') {
        return {
          name: g.name,
          strategy: 'webhook',
          url: g.url,
          ...(g.timeout !== undefined ? { timeout: g.timeout } : {}),
          ...(g.expectedStatus !== undefined ? { expectedStatus: g.expectedStatus } : {}),
        };
      }
      const descriptor = describeJobPoolRef(g.jobPool);
      return {
        name: g.name,
        strategy: 'job',
        jobPool: descriptor,
        ...(g.instructions !== undefined ? { instructions: g.instructions } : {}),
        passCondition: g.passCondition,
      };
    }),
  }));
}
